import { Check, ChevronDown, ChevronRight, Search, Tag } from "lucide-react";
import { Popover as P } from "radix-ui";
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "../lib/cn";
import { Badge } from "./Badge";
import { Button } from "./Button";
import { Field, fieldClass, Input } from "./Input";
import { MenuSearch } from "./OptionMenu";
import { Skeleton } from "./Skeleton";

/** A tag the CRM has, by its full path ("Campaign / Hyperprints"). The id never shows. */
export interface TagPickerOption {
  crm_id: string;
  name: string;
}

/**
 * The category a new tag goes under, as the server takes it: an existing tag, or a new category
 * (itself under an existing tag, or top level). Names are full paths, for display only.
 */
export type TagPickerCategory =
  | { kind: "existing"; crm_id: string; name?: string | null }
  | { kind: "new"; name: string; parent_id?: string | null; parent_name?: string | null };

/** What the picker chose: the campaign's own tag, an existing one, a new one, or none. */
export type TagPickerValue =
  | { mode: "create" }
  | { mode: "link"; crm_id: string; name?: string | null }
  | { mode: "custom"; name: string; category?: TagPickerCategory | null }
  | { mode: "none" };

/** A tag search's answer: the tags, and whether the person's Odoo user may create tags. */
export interface TagSearchResult {
  items: TagPickerOption[];
  can_create?: boolean;
}

/** Searches the CRM's tags (server side). A plain list means creating is allowed. */
export type TagSearchFn = (
  q: string,
  signal: AbortSignal,
) => Promise<TagPickerOption[] | TagSearchResult>;

function asResult(r: TagPickerOption[] | TagSearchResult): TagSearchResult {
  return Array.isArray(r) ? { items: r } : r;
}

export interface TagPickerProps {
  value: TagPickerValue;
  onChange: (value: TagPickerValue) => void;
  /** Searches the CRM's tags (server side). Called with "" when the panel opens. */
  search: TagSearchFn;
  /** The campaign's own tag path, offered first ("Campaign / Hyperprints / Nebraska"). */
  ownPath?: string | null;
  /** The own tag exists in the CRM already (no New badge). */
  ownExists?: boolean;
  "aria-label"?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  /** Opens the panel at once (the field appears because the person asked to change it). */
  defaultOpen?: boolean;
}

type Row =
  | { key: string; kind: "own"; label: string }
  | { key: string; kind: "tag"; tag: TagPickerOption }
  | { key: string; kind: "create"; label: string }
  | { key: string; kind: "none" };

/**
 * Odoo names a nested tag by its full path ("Campaign / Hyperprints / Nebraska"): the tag is the
 * last part, the rest is its parent category. The one place a path is split for display.
 */
export function splitTagPath(path: string): { name: string; category: string[] } {
  const parts = path
    .split(" / ")
    .map((p) => p.trim())
    .filter(Boolean);
  const name = parts.pop() ?? path.trim();
  return { name, category: parts };
}

/** The tag itself: a neutral pill with the tag icon and the tag's own name. */
export function TagPill({
  name,
  tone = "neutral",
  className,
  children,
}: {
  name: string;
  tone?: "neutral" | "accent";
  className?: string;
  /** Trailing content inside the pill, such as a remove button. */
  children?: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-6 min-w-0 max-w-full shrink-0 items-center gap-1 rounded-pill pl-2 font-medium text-caption",
        children ? "pr-0.5" : "pr-2.5",
        tone === "accent" ? "bg-accent-soft text-accent-ink" : "bg-fill-2 text-ink",
        className,
      )}
    >
      <Tag
        className={cn("size-3 shrink-0", tone === "accent" ? "opacity-70" : "text-ink-3")}
        aria-hidden
      />
      <span className="min-w-0 truncate">{name}</span>
      {children}
    </span>
  );
}

/** A tag's parent category in `ink-3`, its levels joined by chevrons. Nothing for a top-level tag. */
export function TagCategory({ category, className }: { category: string[]; className?: string }) {
  if (!category.length) return null;
  return (
    <span
      className={cn("flex min-w-0 items-center gap-0.5 text-caption text-ink-3", className)}
      title={category.join(" › ")}
    >
      {category.map((c, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: levels are positional
          key={i}
          className={cn(
            "flex items-center gap-0.5",
            i === category.length - 1 ? "min-w-0" : "shrink-0",
          )}
        >
          {i ? <ChevronRight className="size-3 shrink-0 text-ink-4" aria-hidden /> : null}
          <span className="truncate">{c}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * An Odoo tag as people read it: the tag pill (with `New` when it will be made), then its
 * category in `ink-3`. `spread` pushes the category to the right edge (picker rows) and keeps up
 * to 45% of the row for it, so a long tag name truncates before its category disappears.
 */
export function TagLabel({
  path,
  isNew = false,
  spread = false,
  className,
}: {
  path: string;
  isNew?: boolean;
  spread?: boolean;
  className?: string;
}) {
  const { name, category } = splitTagPath(path);
  return (
    <span className={cn("flex min-w-0 items-center gap-2", spread && "flex-1", className)}>
      <TagPill name={name} className={spread ? "shrink" : undefined} />
      {isNew ? <Badge tone="outline">New</Badge> : null}
      <TagCategory
        category={category}
        className={spread ? "ml-auto max-w-[45%] shrink-0 justify-end overflow-hidden" : undefined}
      />
    </span>
  );
}

/** A tag's full name for a hover title: "Nebraska (Campaign › Hyperprints)". */
export function tagTitle(path: string): string {
  const { name, category } = splitTagPath(path);
  return category.length ? `${name} (${category.join(" › ")})` : name;
}

function sameName(a: string, b: string) {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** The typed text names this tag: its whole path, or its own name. */
function namesTag(t: TagPickerOption, typed: string) {
  return sameName(t.name, typed) || sameName(splitTagPath(t.name).name, typed);
}

/** The full path of a category the server takes ("Events / Fairs"). */
export function tagCategoryPath(c: TagPickerCategory | null | undefined): string {
  if (!c) return "";
  if (c.kind === "existing") return c.name ?? "";
  return c.parent_name ? `${c.parent_name} / ${c.name}` : c.name;
}

/** The full path of a new tag in a category ("Events / Fairs / Omaha"). */
export function newTagPath(name: string, c: TagPickerCategory | null | undefined): string {
  const p = tagCategoryPath(c);
  return p ? `${p} / ${name}` : name;
}

// The create step ----------------------------------------------------------------------------

/** The category picked in the create step. `preset` is one the caller offers (an import's campaign tag). */
export type TagCreateCategory =
  | { kind: "none" }
  | { kind: "preset"; key: string; path: string; isNew?: boolean }
  | { kind: "existing"; crm_id: string; path: string }
  | { kind: "new"; name: string; under: { crm_id: string; path: string } | null };

/** The category's full path for display; "" for none. */
export function createCategoryPath(c: TagCreateCategory): string {
  if (c.kind === "none") return "";
  if (c.kind === "new") return c.under ? `${c.under.path} / ${c.name}` : c.name;
  return c.path;
}

/** The server's form of a picked category: `null` for none and for a preset. */
export function toTagPickerCategory(c: TagCreateCategory): TagPickerCategory | null {
  if (c.kind === "existing") return { kind: "existing", crm_id: c.crm_id, name: c.path };
  if (c.kind === "new")
    return {
      kind: "new",
      name: c.name,
      parent_id: c.under?.crm_id ?? null,
      parent_name: c.under?.path ?? null,
    };
  return null;
}

type CatRow =
  | { key: string; kind: "none" }
  | { key: string; kind: "preset"; preset: Extract<TagCreateCategory, { kind: "preset" }> }
  | { key: string; kind: "tag"; tag: TagPickerOption }
  | { key: string; kind: "new"; label: string };

/** A small search for a category inside the create step: rows under the box, Enter picks. */
function CategorySearch({
  id,
  label,
  search,
  allowNew,
  presets = [],
  onPick,
}: {
  id: string;
  label: string;
  search: TagSearchFn;
  allowNew: boolean;
  presets?: Extract<TagCreateCategory, { kind: "preset" }>[];
  onPick: (c: TagCreateCategory) => void;
}) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<TagPickerOption[]>([]);
  const [state, setState] = useState<"idle" | "loading" | "error">("loading");
  const [active, setActive] = useState(0);
  const listId = useId();

  useEffect(() => {
    const ctl = new AbortController();
    setState("loading");
    const t = setTimeout(() => {
      search(q.trim(), ctl.signal)
        .then((r) => {
          if (ctl.signal.aborted) return;
          setItems(asResult(r).items);
          setState("idle");
        })
        .catch(() => {
          if (!ctl.signal.aborted) setState("error");
        });
    }, 150);
    return () => {
      clearTimeout(t);
      ctl.abort();
    };
  }, [q, search]);

  const rows = useMemo<CatRow[]>(() => {
    const typed = q.trim();
    const low = typed.toLowerCase();
    const out: CatRow[] = [];
    if (!typed) out.push({ key: "none", kind: "none" });
    for (const p of presets)
      if (!typed || p.path.toLowerCase().includes(low))
        out.push({ key: `preset:${p.key}`, kind: "preset", preset: p });
    for (const t of items.slice(0, 30)) out.push({ key: `tag:${t.crm_id}`, kind: "tag", tag: t });
    const exact = items.some((t) => namesTag(t, typed));
    if (allowNew && typed && !typed.includes("/") && typed.length <= 80 && !exact)
      out.push({ key: "new", kind: "new", label: typed });
    return out;
  }, [items, q, presets, allowNew]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the highlight when results change
  useEffect(() => setActive(0), [rows.length, q]);

  const pick = (r: CatRow) => {
    if (r.kind === "none") onPick({ kind: "none" });
    else if (r.kind === "preset") onPick(r.preset);
    else if (r.kind === "tag") onPick({ kind: "existing", crm_id: r.tag.crm_id, path: r.tag.name });
    else onPick({ kind: "new", name: r.label, under: null });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = rows[active];
      if (r) pick(r);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <Input
        id={id}
        autoFocus
        role="combobox"
        aria-label={label}
        aria-expanded
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={rows[active] ? `${listId}-${active}` : undefined}
        placeholder="Search Odoo tags"
        leading={<Search />}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <div
        id={listId}
        role="listbox"
        aria-label={label}
        className="max-h-[168px] overflow-y-auto rounded-md bg-fill-1 p-1"
      >
        {state === "error" ? (
          <p className="px-2.5 py-1.5 text-caption text-danger-ink">Couldn't search Odoo tags.</p>
        ) : null}
        {rows.map((r, i) => (
          <div
            key={r.key}
            id={`${listId}-${i}`}
            role="option"
            tabIndex={-1}
            aria-selected={i === active}
            onMouseMove={() => setActive(i)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pick(r)}
            onKeyDown={() => undefined}
            title={r.kind === "tag" ? tagTitle(r.tag.name) : undefined}
            className={cn(
              "flex h-8 cursor-default items-center gap-2 overflow-hidden whitespace-nowrap rounded-md px-2.5 text-ui",
              i === active && "bg-fill-2",
            )}
          >
            {r.kind === "none" ? (
              <span className="text-ink-2">No category</span>
            ) : r.kind === "preset" ? (
              <TagLabel path={r.preset.path} isNew={r.preset.isNew} spread />
            ) : r.kind === "tag" ? (
              <TagLabel path={r.tag.name} spread />
            ) : (
              <span className="truncate">New category "{r.label}"</span>
            )}
          </div>
        ))}
        {state === "loading" && items.length === 0 ? (
          <div aria-hidden className="flex flex-col gap-1 px-2.5 py-1.5">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The chosen category as a field: its pill and category, or "No category". */
function CategoryButton({
  id,
  label,
  value,
  onOpen,
}: {
  id: string;
  label: string;
  value: TagCreateCategory;
  onOpen: () => void;
}) {
  let shown: ReactNode;
  if (value.kind === "none") shown = <span className="text-ink-3">No category</span>;
  else
    shown = (
      <TagLabel
        path={createCategoryPath(value)}
        isNew={value.kind === "new" || (value.kind === "preset" && value.isNew)}
      />
    );
  return (
    <button
      id={id}
      type="button"
      aria-label={label}
      onClick={onOpen}
      className={cn(
        fieldClass,
        "flex h-8 items-center gap-2 pr-2 pl-3 text-left",
        "focus-visible:shadow-[inset_0_0_0_1px_var(--vd-focus),0_0_0_3px_var(--vd-accent-soft)]",
      )}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">{shown}</span>
      <ChevronDown className="size-4 shrink-0 text-ink-3" aria-hidden />
    </button>
  );
}

export interface TagCreateFormProps {
  /** Prefills the name (what the person typed). */
  initialName: string;
  /** Searches existing tags to offer as categories. */
  search: TagSearchFn;
  /** Categories the caller offers first (an import's campaign tag). */
  presets?: Extract<TagCreateCategory, { kind: "preset" }>[];
  initialCategory?: TagCreateCategory;
  onCreate: (tag: { name: string; category: TagCreateCategory }) => void;
  /** Esc and Cancel: back to the list. */
  onCancel: () => void;
  className?: string;
}

/** A tag name typed by a person: 1 to 80 characters, no slash. The reason when it isn't. */
function nameProblem(name: string): string | null {
  if (name.includes("/")) return "A tag name can't contain a slash.";
  if (name.trim().length > 80) return "Keep a tag name under 80 characters.";
  return null;
}

/**
 * The create step: the tag's name, its category (an existing tag, none, or a new category that
 * may sit under an existing tag), and a preview of the pill with its category. Enter creates;
 * Esc closes an open category search, then goes back.
 */
export function TagCreateForm({
  initialName,
  search,
  presets,
  initialCategory = { kind: "none" },
  onCreate,
  onCancel,
  className,
}: TagCreateFormProps) {
  const [name, setName] = useState(initialName.replace(/\//g, " ").trim());
  const [category, setCategory] = useState<TagCreateCategory>(initialCategory);
  const [editing, setEditing] = useState<"category" | "under" | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const problem = nameProblem(name);
  const ready = name.trim().length > 0 && !problem;
  const path = createCategoryPath(category);
  const preview = path ? `${path} / ${name.trim()}` : name.trim();

  const create = () => {
    if (ready) onCreate({ name: name.trim(), category });
  };

  // Esc belongs to this step: it closes an open category search, else goes back to the list.
  // Caught on the window before a surrounding popover or dialog sees it and closes.
  const escRef = useRef<() => void>(() => undefined);
  escRef.current = () => {
    if (editing) {
      setEditing(null);
      nameRef.current?.focus();
    } else onCancel();
  };
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || !formRef.current?.contains(e.target as Node)) return;
      e.preventDefault();
      e.stopPropagation();
      escRef.current();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  const picked = (c: TagCreateCategory) => {
    setCategory(c);
    setEditing(null);
    nameRef.current?.focus();
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: a labelled group of fields, not a form fieldset
    <div
      ref={formRef}
      role="group"
      aria-label="New Odoo tag"
      className={cn("flex flex-col gap-3 p-1.5", className)}
    >
      <Field label="Tag name" error={problem ?? undefined}>
        {(id) => (
          <Input
            id={id}
            ref={nameRef}
            autoFocus={editing === null}
            value={name}
            invalid={Boolean(problem)}
            maxLength={81}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                create();
              }
            }}
          />
        )}
      </Field>
      <Field label="Category">
        {(id) =>
          editing === "category" ? (
            <CategorySearch
              id={id}
              label="Category"
              search={search}
              allowNew
              presets={presets}
              onPick={picked}
            />
          ) : (
            <CategoryButton
              id={id}
              label="Category"
              value={category}
              onOpen={() => setEditing("category")}
            />
          )
        }
      </Field>
      {category.kind === "new" ? (
        <Field label="Under">
          {(id) =>
            editing === "under" ? (
              <CategorySearch
                id={id}
                label="Under"
                search={search}
                allowNew={false}
                onPick={(u) =>
                  picked({
                    kind: "new",
                    name: category.name,
                    under: u.kind === "existing" ? { crm_id: u.crm_id, path: u.path } : null,
                  })
                }
              />
            ) : (
              <CategoryButton
                id={id}
                label="Under"
                value={
                  category.under
                    ? { kind: "existing", crm_id: category.under.crm_id, path: category.under.path }
                    : { kind: "none" }
                }
                onOpen={() => setEditing("under")}
              />
            )
          }
        </Field>
      ) : null}
      {/* biome-ignore lint/a11y/useSemanticElements: the preview is a labelled group, not fields */}
      <div
        role="group"
        aria-label="Preview"
        className="flex h-9 min-w-0 items-center rounded-md bg-fill-1 px-2.5"
      >
        {name.trim() ? (
          <TagLabel path={preview} isNew />
        ) : (
          <span className="text-caption text-ink-3">No name</span>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" disabled={!ready} onClick={create}>
          Create
        </Button>
      </div>
    </div>
  );
}

// The picker ---------------------------------------------------------------------------------

/**
 * Picks an Odoo tag, each shown as its pill and category: the campaign's own tag first, then
 * existing tags that match what was typed, then "Create tag" for a new one (which opens the
 * create step in the same panel), then "No tag". Searches on the server.
 */
export function TagPicker({
  value,
  onChange,
  search,
  ownPath,
  ownExists = false,
  "aria-label": ariaLabel = "Odoo tag",
  id,
  disabled,
  className,
  defaultOpen = false,
}: TagPickerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [q, setQ] = useState("");
  const [items, setItems] = useState<TagPickerOption[]>([]);
  const [canCreate, setCanCreate] = useState(true);
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  const [active, setActive] = useState(0);
  const [attempt, setAttempt] = useState(0);
  /** The create step's prefilled name, while it is open. */
  const [creating, setCreating] = useState<string | null>(null);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  // Searches 150 ms after typing stops; each new search cancels the last.
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt re-runs a failed search
  useEffect(() => {
    if (!open) return;
    const ctl = new AbortController();
    setState("loading");
    const t = setTimeout(() => {
      search(q.trim(), ctl.signal)
        .then((found) => {
          if (ctl.signal.aborted) return;
          const r = asResult(found);
          setItems(r.items);
          setCanCreate(r.can_create !== false);
          setState("idle");
        })
        .catch(() => {
          if (!ctl.signal.aborted) setState("error");
        });
    }, 150);
    return () => {
      clearTimeout(t);
      ctl.abort();
    };
  }, [open, q, search, attempt]);

  const typed = q.trim();
  const exact =
    items.some((t) => namesTag(t, typed)) ||
    (ownPath ? sameName(ownPath, typed) || sameName(splitTagPath(ownPath).name, typed) : false);
  const fresh = Boolean(typed) && !typed.includes("/") && !exact;

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    if (ownPath && (!typed || ownPath.toLowerCase().includes(typed.toLowerCase())))
      out.push({ key: "own", kind: "own", label: ownPath });
    for (const t of items) {
      if (ownPath && sameName(t.name, ownPath)) continue;
      out.push({ key: `tag:${t.crm_id}`, kind: "tag", tag: t });
    }
    if (fresh && canCreate) out.push({ key: "create", kind: "create", label: typed });
    out.push({ key: "none", kind: "none" });
    return out;
  }, [items, typed, ownPath, fresh, canCreate]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the highlight when results change
  useEffect(() => setActive(0), [rows.length, q]);

  const close = useCallback(() => {
    setOpen(false);
    setQ("");
    setCreating(null);
  }, []);

  const pick = useCallback(
    (r: Row) => {
      if (r.kind === "create") {
        setCreating(r.label);
        return;
      }
      if (r.kind === "own") onChange({ mode: "create" });
      else if (r.kind === "tag") onChange({ mode: "link", crm_id: r.tag.crm_id, name: r.tag.name });
      else onChange({ mode: "none" });
      close();
    },
    [onChange, close],
  );

  const backToList = () => {
    setCreating(null);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const r = rows[active];
      if (r) {
        e.preventDefault();
        pick(r);
      }
    }
  };

  const isSelected = (r: Row) =>
    (r.kind === "own" && value.mode === "create") ||
    (r.kind === "tag" && value.mode === "link" && value.crm_id === r.tag.crm_id) ||
    (r.kind === "none" && value.mode === "none");

  let shown: ReactNode;
  if (value.mode === "create" && ownPath) {
    shown = <TagLabel path={ownPath} isNew={!ownExists} />;
  } else if (value.mode === "link") {
    shown = <TagLabel path={value.name ?? "Odoo tag"} />;
  } else if (value.mode === "custom") {
    shown = <TagLabel path={newTagPath(value.name, value.category)} isNew />;
  } else {
    shown = <span className="text-ink-3">No tag</span>;
  }

  return (
    <P.Root
      open={open}
      onOpenChange={(o) => {
        if (o) setOpen(true);
        else close();
      }}
    >
      <P.Trigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          className={cn(
            fieldClass,
            "flex h-8 items-center gap-2 pr-2 pl-3 text-left",
            "focus-visible:shadow-[inset_0_0_0_1px_var(--vd-focus),0_0_0_3px_var(--vd-accent-soft)]",
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">{shown}</span>
          <ChevronDown className="size-4 shrink-0 text-ink-3" aria-hidden />
        </button>
      </P.Trigger>
      <P.Portal>
        <P.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
          className={cn(
            "z-50 flex w-[var(--radix-popover-trigger-width)] min-w-[320px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-lg bg-overlay text-ink shadow-popover outline-none",
            "data-[state=closed]:animate-vd-fade-out data-[state=open]:animate-vd-pop-in",
          )}
        >
          {creating !== null ? (
            <TagCreateForm
              className="m-1"
              initialName={creating}
              search={search}
              onCancel={backToList}
              onCreate={({ name, category }) => {
                onChange({ mode: "custom", name, category: toTagPickerCategory(category) });
                close();
              }}
            />
          ) : (
            <>
              <MenuSearch
                ref={inputRef}
                autoFocus
                role="combobox"
                aria-label="Search Odoo tags"
                aria-expanded
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={rows[active] ? `${listId}-${active}` : undefined}
                placeholder="Search Odoo tags"
                value={q}
                onValueChange={setQ}
                onKeyDown={onKeyDown}
              />
              <div
                id={listId}
                role="listbox"
                aria-label="Odoo tags"
                className="max-h-[min(328px,var(--radix-popover-content-available-height))] overflow-y-auto overscroll-contain p-1"
              >
                {state === "error" ? (
                  <div className="flex items-center justify-between gap-2 px-2.5 py-2">
                    <span className="text-caption text-danger-ink">Couldn't search Odoo tags.</span>
                    <Button size="sm" variant="ghost" onClick={() => setAttempt((n) => n + 1)}>
                      Try again
                    </Button>
                  </div>
                ) : null}
                {rows.map((r, i) => (
                  <div
                    key={r.key}
                    id={`${listId}-${i}`}
                    role="option"
                    tabIndex={-1}
                    aria-selected={i === active}
                    onMouseMove={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(r)}
                    onKeyDown={() => undefined}
                    title={
                      r.kind === "own"
                        ? tagTitle(r.label)
                        : r.kind === "tag"
                          ? tagTitle(r.tag.name)
                          : undefined
                    }
                    className={cn(
                      "flex h-8 cursor-default items-center gap-2 overflow-hidden whitespace-nowrap rounded-sm px-2 text-ui",
                      i === active && "bg-fill-2",
                    )}
                  >
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      {r.kind === "own" ? (
                        <TagLabel path={r.label} isNew={!ownExists} spread />
                      ) : r.kind === "tag" ? (
                        <TagLabel path={r.tag.name} spread />
                      ) : r.kind === "create" ? (
                        <span className="truncate">Create tag "{r.label}"</span>
                      ) : (
                        <span className="text-ink-2">No tag</span>
                      )}
                    </span>
                    {isSelected(r) ? (
                      <Check className="size-4 shrink-0 text-ink-2" aria-hidden />
                    ) : null}
                  </div>
                ))}
                {state === "loading" && items.length === 0 ? (
                  <div aria-hidden className="flex flex-col gap-1 px-2.5 py-1.5">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ) : null}
              </div>
              {state === "idle" && fresh && !canCreate ? (
                <p className="border-hairline border-t px-3 py-2 text-caption text-ink-3">
                  Your Odoo user can't create tags.
                </p>
              ) : null}
            </>
          )}
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}
